import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';

export default function OrderViewPage() {
    const { orderId } = useParams();
    const { getOrderById } = useAppContext();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const loadOrder = async () => {
            try {
                setLoading(true);
                const orderData = await getOrderById(orderId);
                if (orderData) {
                    setOrder(orderData);
                } else {
                    setError('Pedido não encontrado');
                }
            } catch (err) {
                setError('Erro ao carregar pedido');
            } finally {
                setLoading(false);
            }
        };

        if (orderId) {
            loadOrder();
        }
    }, [orderId, getOrderById]);

    const getStatusInfo = (status) => {
        const statusConfig = {
            'pendente': { bg: 'warning', icon: 'bi-clock-history', label: 'Aguardando confirmação' },
            'confirmado': { bg: 'info', icon: 'bi-check-circle', label: 'Pedido confirmado' },
            'preparando': { bg: 'primary', icon: 'bi-gear', label: 'Em preparação' },
            'saiu-entrega': { bg: 'secondary', icon: 'bi-truck', label: 'Saiu para entrega' },
            'entregue': { bg: 'success', icon: 'bi-check-circle-fill', label: 'Pedido entregue' },
            'cancelado': { bg: 'danger', icon: 'bi-x-circle-fill', label: 'Pedido cancelado' },
        };

        return statusConfig[status] || statusConfig['pendente'];
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getPackageTypeLabel = (packageType) => {
        if (packageType === 'package') return 'Bandeja';
        if (packageType === 'cento') return 'Cento';
        return 'Unidade';
    };

    if (loading) {
        return (
            <div className="container mt-5">
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                    </div>
                    <p className="mt-3">Carregando pedido...</p>
                </div>
            </div>
        );
    }

    if (error || !order) {
        return (
            <div className="container mt-5">
                <div className="alert alert-danger text-center">
                    <h4><i className="bi bi-exclamation-circle me-2"></i>{error || 'Pedido não encontrado'}</h4>
                    <p className="mb-0">Verifique se o link está correto</p>
                </div>
            </div>
        );
    }

    const statusInfo = getStatusInfo(order.status);

    return (
        <div className="container mt-4 mb-5" style={{ maxWidth: '800px' }}>
            {/* Header */}
            <div className="text-center mb-4">
                <h1 className="h3 mb-2">MicroGestor</h1>
                <p className="text-muted">Acompanhe seu pedido</p>
            </div>

            {/* Status do pedido */}
            <div className={`card shadow-sm mb-4 border-${statusInfo.bg}`}>
                <div className={`card-body text-center bg-${statusInfo.bg} bg-opacity-10`}>
                    <div className="display-1 mb-2">{statusInfo.icon}</div>
                    <h2 className="h4 mb-1">{statusInfo.label}</h2>
                    <p className="text-muted mb-0">Pedido {order.orderNumber}</p>
                </div>
            </div>

            {/* Informações do pedido */}
            <div className="card shadow-sm mb-4">
                <div className="card-body">
                    <h3 className="h5 mb-3"><i className="bi bi-clipboard me-2"></i>Informações do Pedido</h3>

                    <div className="row mb-3">
                        <div className="col-6">
                            <div className="small text-muted">Cliente</div>
                            <div className="fw-semibold">{order.clientName}</div>
                        </div>
                        <div className="col-6">
                            <div className="small text-muted">Telefone</div>
                            <div className="fw-semibold">{order.clientPhone || 'Não informado'}</div>
                        </div>
                    </div>

                    <div className="mb-3">
                        <div className="small text-muted">Endereço de entrega</div>
                        <div className="fw-semibold">
                            {order.clientAddress || 'Não informado'}
                            {order.clientNeighborhood && <>, {order.clientNeighborhood}</>}
                            {order.clientCity && <> - {order.clientCity}</>}
                        </div>
                        {order.clientReference && (
                            <div className="small text-muted">
                                <i className="bi bi-geo-alt me-1"></i>Referência: {order.clientReference}
                            </div>
                        )}
                    </div>

                    <div className="row">
                        <div className="col-6">
                            <div className="small text-muted">Data de entrega</div>
                            <div className="fw-semibold">
                                <i className="bi bi-calendar me-1"></i>{formatDate(order.deliveryDate)}
                            </div>
                        </div>
                        {order.deliveryTime && (
                            <div className="col-6">
                                <div className="small text-muted">Horário</div>
                                <div className="fw-semibold"><i className="bi bi-clock me-1"></i>{order.deliveryTime}</div>
                            </div>
                        )}
                    </div>

                    {order.deliveryNotes && (
                        <div className="mt-3 p-2 bg-light rounded">
                            <div className="small text-muted">Observações</div>
                            <div>{order.deliveryNotes}</div>
                        </div>
                    )}
                </div>
            </div>

            {/* Itens do pedido */}
            <div className="card shadow-sm mb-4">
                <div className="card-body">
                    <h3 className="h5 mb-3"><i className="bi bi-cart me-2"></i>Itens do Pedido</h3>

                    <div className="list-group list-group-flush">
                        {order.items.map((item, index) => (
                            <div key={index} className="list-group-item px-0">
                                <div className="d-flex justify-content-between align-items-start">
                                    <div className="flex-grow-1">
                                        <div className="fw-semibold">{item.productName}</div>
                                        <div className="small text-muted">
                                            {item.quantity} {getPackageTypeLabel(item.packageType)}
                                            {item.packageType !== 'unit' && ` (${item.actualQuantity} unidades)`}
                                            {' × R$ '}{item.pricePerUnit.toFixed(2)}
                                        </div>
                                    </div>
                                    <div className="text-end">
                                        <div className="fw-bold">R$ {item.totalPrice.toFixed(2)}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-3 pt-3 border-top">
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="h5 mb-0">Total do Pedido</span>
                            <span className="h4 mb-0 text-success fw-bold">
                                R$ {order.totalAmount.toFixed(2)}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Timeline de status */}
            <div className="card shadow-sm">
                <div className="card-body">
                    <h3 className="h6 mb-3"><i className="bi bi-geo-alt me-2"></i>Acompanhamento</h3>

                    <div className="timeline">
                        <div className={order.status === 'pendente' || order.status === 'confirmado' || order.status === 'preparando' || order.status === 'saiu-entrega' || order.status === 'entregue' ? 'timeline-item active' : 'timeline-item'}>
                            <div className="timeline-marker"><i className="bi bi-clock-history"></i></div>
                            <div className="timeline-content">
                                <div className="small">Pedido recebido</div>
                            </div>
                        </div>
                        <div className={order.status === 'confirmado' || order.status === 'preparando' || order.status === 'saiu-entrega' || order.status === 'entregue' ? 'timeline-item active' : 'timeline-item'}>
                            <div className="timeline-marker"><i className="bi bi-check-circle"></i></div>
                            <div className="timeline-content">
                                <div className="small">Pedido confirmado</div>
                            </div>
                        </div>
                        <div className={order.status === 'preparando' || order.status === 'saiu-entrega' || order.status === 'entregue' ? 'timeline-item active' : 'timeline-item'}>
                            <div className="timeline-marker"><i className="bi bi-gear"></i></div>
                            <div className="timeline-content">
                                <div className="small">Em preparação</div>
                            </div>
                        </div>
                        <div className={order.status === 'saiu-entrega' || order.status === 'entregue' ? 'timeline-item active' : 'timeline-item'}>
                            <div className="timeline-marker"><i className="bi bi-truck"></i></div>
                            <div className="timeline-content">
                                <div className="small">Saiu para entrega</div>
                            </div>
                        </div>
                        <div className={order.status === 'entregue' ? 'timeline-item active' : 'timeline-item'}>
                            <div className="timeline-marker"><i className="bi bi-check-circle-fill"></i></div>
                            <div className="timeline-content">
                                <div className="small">Entregue</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="text-center mt-4 text-muted small">
                <p className="mb-0">Pedido criado em {new Date(order.createdAt).toLocaleString('pt-BR')}</p>
                <p className="mb-0">Em caso de dúvidas, entre em contato pelo telefone informado no pedido</p>
            </div>
        </div>
    );
}
